import {Form, Formik} from 'formik'
import {Dispatch, SetStateAction, useEffect, useState} from 'react'
import {Modal} from 'react-bootstrap'
import {useIntl} from 'react-intl'
import {SelectInput} from '../../widgets/components/Input/SelectInput'
import TextInput from '../../widgets/components/Input/TextInput'
import {BasicButton} from '../../widgets/components/UI/BasicButton'
import {CustomButton} from '../../widgets/components/UI/CustomButton'
import {InvestmentConfirmationInitialValues, termSheetStatusValues} from '../core/_constants'
import * as Yup from 'yup'
import {
  createTransactionApi,
  getAllColumnsFromRoundIdIU,
  getInvestorResponseAPI,
} from '../core/_requests'
import {useAuth} from '../../auth'
import TextArea from '../../widgets/components/Input/TextArea'
import {ToolTipUI} from '../../widgets/components/UI/ToolTipUI'
import {toast} from 'react-toastify'
import {Spinner} from '../../widgets/components/General/Spinner'
import TagManager from 'react-gtm-module'
import {GTMEvent} from '../../../hooks/gtmHelpers'

interface CurrencyModel {
  id: number
  name: string
  value: number
}

export const InvestmentConfirmationModal = ({
  modalShow,
  setModalShow,
  currency,
  roundId,
  roundName,
  investorId,
  investorUserId,
  investorUsers,
  currencyOptions,
  getInvestor,
  setColumns,
  setCurrentStatus,
  partialStatus,
  callGetAllColumnAPI,
  getActiveRoundFromApi,
}: {
  modalShow: boolean
  setModalShow: Dispatch<SetStateAction<boolean>>
  currency: number
  roundId: number
  roundName: string | undefined
  investorId: number
  investorUserId: string | undefined
  investorUsers: any
  currencyOptions: CurrencyModel[]
  getInvestor?: () => void
  callGetAllColumnAPI?: boolean
  setColumns?: React.Dispatch<any>
  setCurrentStatus?: Dispatch<SetStateAction<any>>
  partialStatus?: any
  getActiveRoundFromApi?: () => void
}) => {
  const {formatMessage} = useIntl()
  const [loading, setLoading] = useState(false)
  const [apiloading, setApiLoading] = useState<number>(0)
  const {currentUser, companyId} = useAuth()
  const [formValues, setFormValues] = useState<any>(InvestmentConfirmationInitialValues)
  const [whoCommitted, setWhoCommitted] = useState<any>()
  const [selectedCurrency, setSelectedCurrency] = useState(currentUser?.company[0].currency?.code)

  const intializeInvestmentConfirmationSchema = Yup.object().shape({
    roundId: Yup.string(),
    amount: Yup.number()
      .moreThan(-1, formatMessage({id: "Amount Confirmed can't be negative"}))
      .required(formatMessage({id: 'Amount Confirmed is required'}))
      .integer(formatMessage({id: 'Amount Confirmed must be integer'})),
    investorUserId: Yup.string().required(
      formatMessage({id: 'Contact person who committed is required'})
    ),
    termSheetStatus: Yup.string().required(formatMessage({id: 'Termsheet Status is required'})),
    currencyId: Yup.number(),
    additionalTermsandCoditions: Yup.string(),
  })

  useEffect(() => {
    if (currency) {
      setFormValues({
        ...InvestmentConfirmationInitialValues,
        currencyId: currency,
      })
      if (currencyOptions) {
        const selectedCurrency: CurrencyModel | undefined = currencyOptions.find(
          (curr: CurrencyModel) => curr.id === currency
        )
        if (selectedCurrency) {
          setSelectedCurrency(selectedCurrency.name.substring(0, 4).trim())
        }
      }
    } else {
      setFormValues({
        ...InvestmentConfirmationInitialValues,
        currencyId: '',
      })
    }
  }, [currency, currencyOptions]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const getInvestorResponse = async () => {
      if (roundId && investorId && investorUserId) {
        const {
          data: {data: responseData, success},
        } = await getInvestorResponseAPI(investorId, +investorUserId, roundId)
        if (success && responseData) {
          if (currency) {
            setFormValues({
              ...InvestmentConfirmationInitialValues,
              investorUserId: responseData.investorUserId,
              additionalTermsandCoditions: responseData.additionalTermsandCoditions || '',
              amount: +responseData.amount,
              roundId: responseData.roundId,
              termSheetStatus: responseData.termSheetStatus || '',
              currencyId: currency,
            })
            if (currencyOptions) {
              const selectedCurrency: CurrencyModel | undefined = currencyOptions.find(
                (curr: CurrencyModel) => curr.id === currency
              )
              if (selectedCurrency) {
                setSelectedCurrency(selectedCurrency.name.substring(0, 4).trim())
              }
            }
          } else {
            setFormValues({
              ...InvestmentConfirmationInitialValues,
              investorUserId: responseData.investorUserId,
              additionalTermsandCoditions: responseData.additionalTermsandCoditions,
              amount: responseData.amount,
              roundId: responseData.roundId,
              termSheetStatus: responseData.termSheetStatus,
              currencyId: '',
            })
            setApiLoading((prev) => prev + 1)
          }
        } else {
          setFormValues({
            ...InvestmentConfirmationInitialValues,
            investorUserId: investorUserId || '',
            currencyId: currency || '',
          })
          setApiLoading((prev) => prev + 1)
        }
      }
    }
    if (modalShow) {
      getInvestorResponse()
    }
  }, [modalShow]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (investorUsers?.length > 0) {
      const inverstorUserUpdated = investorUsers.map((investorUser: any) => {
        return {
          id: investorUser.investorUserId,
          name: investorUser.name,
          value: investorUser.investorUserId,
        }
      })
      setWhoCommitted(inverstorUserUpdated)
      setApiLoading((prev) => prev + 1)
    }
  }, [investorUsers])

  const onSubmit = async (values: any) => {
    try {
      setLoading(true)
      if (!companyId) {
        throw formatMessage({id: 'Company ID is required.'})
      }
      values.roundId = +roundId
      values.currencyId = currency
      values.investorId = investorId
      values.investorUserId = +values.investorUserId

      const {
        data: {success, errors},
      } = await createTransactionApi(values)
      if (success) {
        if (getInvestor) {
          getInvestor()
        }
        if (getActiveRoundFromApi) {
          getActiveRoundFromApi()
        }
        if (callGetAllColumnAPI && roundId) {
          const {
            data: {success, data: allColumns},
          } = await getAllColumnsFromRoundIdIU(roundId)
          if (success) {
            setColumns!(allColumns)
          }
        }
        if (partialStatus && setCurrentStatus) {
          setCurrentStatus(partialStatus)
        }

        setModalShow(false)
        toast.success(formatMessage({id: 'Investor confirmation done'}))
        TagManager.dataLayer({
          dataLayer: {
            event: GTMEvent.CRMInvestmentTransactionSuccessful,
            action: 'normal',
            metadata: {
              user_id: currentUser?.userId,
              email: currentUser?.email,
              company_id: companyId,
              round_id: +roundId,
              currency_id: currency,
              investor_id: investorId,
              investor_user_id: +values.investorUserId,
            },
          },
        })
      } else {
        errors.forEach((error: string) => {
          toast.error(formatMessage({id: error}))
        })
        TagManager.dataLayer({
          dataLayer: {
            event: GTMEvent.CRMInvestmentTransactionFailed,
            action: 'normal',
            metadata: {
              user_id: currentUser?.userId,
              email: currentUser?.email,
              company_id: companyId,
              round_id: +roundId,
              currency_id: currency,
              investor_id: investorId,
              investor_user_id: +values.investorUserId,
              error: errors[0],
            },
          },
        })
      }
    } catch (error) {
      console.error(error)
      TagManager.dataLayer({
        dataLayer: {
          event: GTMEvent.CRMInvestmentTransactionFailed,
          action: 'normal',
          metadata: {
            user_id: currentUser?.userId,
            email: currentUser?.email,
            company_id: companyId,
            round_id: +roundId,
            currency_id: currency,
            investor_id: investorId,
            investor_user_id: +values.investorUserId,
            error: error,
          },
        },
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <Modal
        size='lg'
        show={modalShow}
        onHide={() => setModalShow(false)}
        aria-labelledby='contained-modal-title-vcenter'
        centered
      >
        {apiloading < 2 && modalShow && <Spinner />}
        <Modal.Header closeButton>
          <div className='fs-4 fw-bold'>
            {formatMessage({id: 'Investment Confirmation Details'})}
          </div>
        </Modal.Header>
        <Formik
          validationSchema={intializeInvestmentConfirmationSchema}
          initialValues={formValues}
          enableReinitialize={true}
          onSubmit={onSubmit}
        >
          {(formik) => {
            return (
              <Form>
                <Modal.Body>
                  <div className='row d-md-flex justify-content-between'>
                    <div className='col-md-6'>
                      <div className='fv-row mb-7'>
                        <label className='form-label font-size-13 text-dark text-capitalize ms-3 ms-md-0'>
                          {formatMessage({id: 'Round Name'})}*{' '}
                          <ToolTipUI
                            tooltipText={formatMessage({
                              id: 'GLOBAL.TOOLTIP.INITIALIZE_ROUND.ROUND_TYPE',
                            })}
                          />
                        </label>
                        <select
                          disabled
                          className='form-select form-select-lg  h-40px font-size-13'
                          placeholder='select Round'
                        >
                          <option value={roundId}>{roundName}</option>
                        </select>
                      </div>
                    </div>
                    <div className='col-md-6'>
                      <SelectInput
                        label={formatMessage({id: 'Contact person who committed'})}
                        fieldName={'investorUserId'}
                        placeholder={formatMessage({id: 'Select the contact name who said yes'})}
                        formik={formik}
                        toolTipText={formatMessage({
                          id: 'GLOBAL.TOOLTIP.INVESTOR.COMMITTED_PERSON',
                        })}
                        options={whoCommitted}
                        disabled={!!investorUserId}
                      />
                    </div>
                  </div>
                  <div className='row d-md-flex justify-content-between'>
                    <div className='col-md-6'>
                      <TextInput
                        fieldType={'number'}
                        label={`${formatMessage({id: 'Amount Confirmed'})}(${selectedCurrency})`}
                        fieldName={'amount'}
                        placeholder={`${formatMessage({
                          id: 'Enter amount in',
                        })} ${selectedCurrency}`}
                        formik={formik}
                        toolTipText={formatMessage({
                          id: 'GLOBAL.TOOLTIP.INVESTOR.AMOUNT_CONFIRMED',
                        })}
                        isStarRequired={true}
                      />
                    </div>

                    {/* <div className='col-md-6'>
                      <SelectInput
                        label={formatMessage({id: 'Select Currency'})}
                        fieldName={'currencyId'}
                        placeholder={formatMessage({id: 'Select the currency'})}
                        formik={formik}
                        toolTipText={formatMessage({
                          id: 'GLOBAL.TOOLTIP.INITIALIZE_ROUND.SELECT_CURRENCY',
                        })}
                        options={currencyOptions}
                        disabled={!!currency}
                      />
                      </div> */}

                    <div className='col-md-6'>
                      <SelectInput
                        label={formatMessage({id: 'Termsheet Status'})}
                        fieldName={'termSheetStatus'}
                        placeholder={formatMessage({id: 'Select Termsheet Status'})}
                        formik={formik}
                        toolTipText={formatMessage({
                          id: 'GLOBAL.TOOLTIP.INVESTOR.TERMSHEET_STATUS',
                        })}
                        options={termSheetStatusValues}
                      />
                    </div>
                  </div>

                  <div>
                    <TextArea
                      fieldName={'additionalTermsandCoditions'}
                      formik={formik}
                      placeholder={formatMessage({
                        id: 'Any Additional Terms & Conditions?',
                      })}
                      label={formatMessage({id: 'Any Additional Terms & Conditions?'})}
                      toolTipText={formatMessage({
                        id: 'GLOBAL.TOOLTIP.INVESTOR.ANY_ADDITIONALDETAILS',
                      })}
                      isTooltipNotRequired={true}
                    />
                  </div>
                </Modal.Body>
                <Modal.Footer>
                  <div className='w-100 d-flex justify-content-md-end justify-content-around'>
                    <BasicButton
                      buttonText={formatMessage({id: 'Cancel'})}
                      onClick={() => setModalShow(false)}
                      border='none'
                      color='#F5F8FA'
                      textColor='#5E6278'
                      height='44'
                      padding='12px 22px'
                      minWidth={90}
                    />
                    <CustomButton
                      isSubmitting={formik.isSubmitting}
                      isValid={formik.isValid}
                      buttonText={formatMessage({id: 'Confirm'})}
                      loading={loading}
                      width={2}
                      marginButtom={'mb-0 ms-3'}
                      widthLoading={4}
                      height={44}
                    />
                  </div>
                </Modal.Footer>
              </Form>
            )
          }}
        </Formik>
      </Modal>
    </>
  )
}
